import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import TimePicker from './TimePicker';

type DayFormData = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  notes: string;
};

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (scheduleData: any) => void | Promise<void>;
  schedule?: any;
  /** Full weekly schedule list so Day of Week changes can load that day's hours. */
  schedules?: any[];
}

const emptyDayForm = (dayOfWeek = 0, isAvailable = true): DayFormData => ({
  dayOfWeek,
  startTime: '',
  endTime: '',
  isAvailable,
  notes: ''
});

const buildDayForm = (daySchedule: any | undefined, dayOfWeek: number): DayFormData => {
  if (!daySchedule) {
    return emptyDayForm(dayOfWeek, false);
  }

  const start = String(daySchedule.startTime ?? '').trim();
  const end = String(daySchedule.endTime ?? '').trim();
  const hasFilledTimes = Boolean(start && end);
  // Days with no saved hours — leave "Available" off until the user opts in.
  const isAvailable =
    daySchedule.isAvailable !== undefined ? daySchedule.isAvailable : hasFilledTimes;

  return {
    dayOfWeek,
    startTime: daySchedule.startTime || '',
    endTime: daySchedule.endTime || '',
    isAvailable,
    notes: daySchedule.notes || ''
  };
};

const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose, onSave, schedule, schedules }) => {
  const [formData, setFormData] = useState<DayFormData>(emptyDayForm());
  const [dayDrafts, setDayDrafts] = useState<Record<number, DayFormData>>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  useEffect(() => {
    if (schedule) {
      const dayOfWeek = schedule.dayOfWeek ?? 0;
      const initial = buildDayForm(schedule, dayOfWeek);
      setFormData(initial);
      setDayDrafts({ [dayOfWeek]: initial });
      setSelectedDays([dayOfWeek]);
      setErrors({});
    } else {
      const initial = emptyDayForm(0, true);
      setFormData(initial);
      setDayDrafts({});
      setSelectedDays([]);
      setErrors({});
    }
  }, [schedule]);

  const patchForm = (patch: Partial<DayFormData>) => {
    setFormData((prev) => {
      const next = { ...prev, ...patch };
      setDayDrafts((drafts) => ({ ...drafts, [next.dayOfWeek]: next }));
      return next;
    });
  };

  const switchToDay = (newDayOfWeek: number) => {
    if (newDayOfWeek === formData.dayOfWeek) return;

    // Day-of-week dropdown navigates to a single day; it is not "Apply to days".
    // Load that day's saved schedule (or an existing draft for it) and select only it.
    const nextForm =
      dayDrafts[newDayOfWeek] ??
      buildDayForm(
        schedules?.find((s) => s.dayOfWeek === newDayOfWeek),
        newDayOfWeek
      );

    setDayDrafts({ [newDayOfWeek]: nextForm });
    setFormData(nextForm);
    setSelectedDays([newDayOfWeek]);
    setErrors({});
  };

  const convertTimeToMinutes = (timeString: string): number => {
    if (!timeString) return 0;

    const [time, period] = timeString.split(' ');
    if (!time || !period) return 0;

    const [hours, minutes] = time.split(':').map(Number);
    let hour24 = hours;

    if (period === 'PM' && hours !== 12) {
      hour24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0;
    }

    return hour24 * 60 + minutes;
  };

  const validateDay = (data: DayFormData): {[key: string]: string} => {
    const newErrors: {[key: string]: string} = {};

    if (data.dayOfWeek < 0 || data.dayOfWeek > 6) {
      newErrors.dayOfWeek = 'Please select a valid day of the week';
    }

    if (data.isAvailable) {
      if (!data.startTime) {
        newErrors.startTime = 'Start time is required when available';
      }

      if (!data.endTime) {
        newErrors.endTime = 'End time is required when available';
      }

      if (data.startTime && data.endTime) {
        const startTime = convertTimeToMinutes(data.startTime);
        const endTime = convertTimeToMinutes(data.endTime);

        if (endTime <= startTime) {
          newErrors.endTime = 'End time must be after start time';
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Persist the currently visible day, then merge any "Apply to days" copies.
    const drafts: Record<number, DayFormData> = {
      ...dayDrafts,
      [formData.dayOfWeek]: formData
    };

    const daysToSave = Array.from(
      new Set([
        ...Object.keys(drafts).map(Number),
        ...(selectedDays.length > 0 ? selectedDays : [formData.dayOfWeek])
      ])
    ).sort((a, b) => a - b);

    for (const day of daysToSave) {
      if (!drafts[day]) {
        // Selected via "Apply to days" without a per-day draft — copy current hours.
        drafts[day] = { ...formData, dayOfWeek: day };
      }
    }

    for (const day of daysToSave) {
      const dayErrors = validateDay(drafts[day]);
      if (Object.keys(dayErrors).length > 0) {
        setDayDrafts(drafts);
        setFormData(drafts[day]);
        setSelectedDays(daysToSave);
        setErrors(dayErrors);
        return;
      }
    }

    try {
      const schedulesData = daysToSave.map((day) => drafts[day]);
      const result = onSave(schedulesData);
      if (result instanceof Promise) {
        await result;
      }
      handleClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error saving schedules:', error);
    }
  };

  const toggleDaySelection = (dayValue: number) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayValue)) {
        // Don't allow deselecting if it's the current day and the only one selected
        if (prev.length === 1 && prev[0] === dayValue && dayValue === formData.dayOfWeek) {
          return prev;
        }

        // Drop the draft for deselected non-current days so Update won't keep them.
        if (dayValue !== formData.dayOfWeek) {
          setDayDrafts((drafts) => {
            const next = { ...drafts };
            delete next[dayValue];
            return next;
          });
        }
        return prev.filter((d) => d !== dayValue);
      }

      // Copy current hours into that day's draft (Apply to days).
      setDayDrafts((drafts) => ({
        ...drafts,
        [formData.dayOfWeek]: formData,
        [dayValue]: { ...formData, dayOfWeek: dayValue }
      }));
      return [...prev, dayValue];
    });
  };

  const handleClose = () => {
    setFormData(emptyDayForm());
    setDayDrafts({});
    setErrors({});
    setSelectedDays([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-visible">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {schedule ? 'Edit Schedule' : 'Set Schedule'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Day of Week */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Day of Week
              </label>
              <select
                value={formData.dayOfWeek}
                onChange={(e) => switchToDay(parseInt(e.target.value, 10))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
                  errors.dayOfWeek ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              >
                {daysOfWeek.map((day) => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
              {errors.dayOfWeek && (
                <p className="mt-1 text-sm text-red-600">{errors.dayOfWeek}</p>
              )}
            </div>

            {/* Availability Toggle */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isAvailable}
                  onChange={(e) => {
                    patchForm({ isAvailable: e.target.checked });
                    // Clear time-related errors when availability changes
                    const newErrors = { ...errors };
                    delete newErrors.startTime;
                    delete newErrors.endTime;
                    setErrors(newErrors);
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Available on this day</span>
              </label>
            </div>

            {/* Time Range */}
            {formData.isAvailable && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time
                    </label>
                    <TimePicker
                      value={formData.startTime}
                      onChange={(time) => {
                        patchForm({ startTime: time });
                        if (errors.startTime) {
                          setErrors({ ...errors, startTime: '' });
                        }
                      }}
                      placeholder="--:-- --"
                      className={errors.startTime ? 'border-red-500' : ''}
                    />
                    {errors.startTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.startTime}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <TimePicker
                      value={formData.endTime}
                      onChange={(time) => {
                        patchForm({ endTime: time });
                        if (errors.endTime) {
                          setErrors({ ...errors, endTime: '' });
                        }
                      }}
                      placeholder="--:-- --"
                      className={errors.endTime ? 'border-red-500' : ''}
                    />
                    {errors.endTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.endTime}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Apply to Multiple Days */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apply to days
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Select days to copy the hours.
              </p>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map((day) => {
                  const isSelected = selectedDays.includes(day.value);
                  const isCurrentDay = formData.dayOfWeek === day.value;
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDaySelection(day.value)}
                      className={`w-12 py-2 rounded-full text-sm font-medium transition-all flex items-center justify-center ${
                        isSelected
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                      } ${isCurrentDay && !isSelected ? 'ring-2 ring-blue-300' : ''}`}
                    >
                      {day.label.substring(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => patchForm({ notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional notes about your availability..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="submit"
                className="aacore-btn2-primary flex-1"
              >
                <Check className="w-4 h-4" />
                {schedule ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
